import { Fragment, useState, useCallback, useMemo, useRef } from "react";
import { floorScopeLabel } from "@/lib/floor-scope-label";
import { groupEquivalentLineItems, getItemSubGroup, ITEM_SUB_GROUP_LABELS, type ItemSubGroupId, getCategorySection, SUB_GROUP_SECTION } from "@workspace/api-zod/work-type";
import { usePageTracking } from "@/hooks/use-track";
import { trackEvent } from "@/App";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation, Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/status-badge";
import { QuoteStatusControl } from "@/components/quote-status-control";
import { BrandingDisplay } from "@/components/branding-display";
import { FormSkeleton } from "@/components/loading-skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { getBuilderQuoteMode, trackBuilderEvent } from "@/lib/analytics";
import { useAuth } from "@/hooks/use-auth";
import { getMarketCurrency, formatMoney } from "@/lib/utils";
import {
  ArrowLeft,
  Send,
  TrendingUp,
  Clock,
  MapPin,
  User,
  FileText,
  CheckCircle2,
  Pencil,
  Copy,
  Check,
  X,
  Plus,
  Trash2,
  Save,
  Share2,
  Mail,
  Building2,
  HardHat,
  Package,
  Truck,
  BrickWall,
  Construction,
  AlertCircle,
  Home,
  Layers,
  LayoutGrid,
  Car,
  Triangle,
  Wrench,
  Hammer,
  Zap,
  Droplets,
  ArrowUpFromLine,
  PaintBucket,
  Landmark,
  Columns,
  CircleDashed,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import { getCostCategories, getProjectTypes, UNITS, getUnits, getCostTrades, type Quote, type QuoteLineItem, type Client, type BuilderProfile } from "@shared/schema";
import { generateQuotePDF } from "@/lib/pdf-generator";
import { PdfTemplateQuoteControls, PdfTemplateDesignPrompt } from "@/components/pdf-template/pdf-template-quote-controls";
import { quoteHeadingLabel } from "@workspace/api-zod/quote-headings";
import { quoteCategoryLabel } from "@/lib/quote-heading-label";
import { buildQuoteExportInputs } from "@/lib/quote-export-inputs";
import { calculateQuoteCostTotals } from "@/lib/quote-cost-totals";
import { quotedMoney, withQuotedCostSplit, type QuotedCostLine } from "@workspace/api-zod/quote-cost-split";
import { applyQuotedPriceEdit } from "@/lib/quoted-price-edit";
import { getCategoryIcon, getItemSortPriority, SUB_GROUP_ICONS } from "@/lib/category-icons";
import { getEffectiveStage, getEffectiveSubGroup, QUOTE_PLACEMENT_STAGE_ORDER } from "@/lib/section-placement";
import { QuoteMaterialsList } from "@/components/quote-materials-list";
import { QuoteMaterialsWorkspace } from "@/components/quote-materials-workspace";
import { QuoteScopeReview } from "@/components/quote-scope-review";
import { QuoteDimensions, QuantityMaterialsReview } from "@/components/quote-dimensions";
import { updateMaterialPrice, setMaterialPackRounding, setMaterialOrderQuantity } from "@workspace/api-zod/materials-list";
import { costRecipeSchema, type CostRecipe } from "@workspace/api-zod/cost-recipe";
import type { QuantityDerivation } from "@workspace/api-zod/quantity-dimensions";
import { recipeAtQuotePrice, quoteRecipeSplit } from "@/components/quote-item-recipe-breakdown";
import { markDimensionPriceReview, priceRecipeFromDimensions as priceRecipeForQuote } from "@workspace/api-zod/dimension-pricing";
import { deriveCostRecipe } from "@workspace/api-zod/cost-recipe";
import { QuoteMaterialPricing } from "@/components/material-dimension-pricing";

interface ValidationFlag {
  type: "scope_gap" | "measurement_issue" | "quantity_concern" | "unit_mismatch";
  itemIndex?: number;
  itemName?: string;
  detail: string;
  suggestion?: string;
  confidence: "high" | "medium" | "low";
  source: "master_builder" | "chartered_engineer";
  suggestedUnitCost?: number;
  suggestedQuantity?: number;
  suggestRemoval?: boolean;
}

interface QuoteWithDetails extends Quote {
  client?: Client;
  lineItems: QuoteLineItem[];
}

interface EditableLineItem {
  materialAllowanceApproval?: unknown;
  quoteCostRecipe?: CostRecipe | null;
  quantityDerivation?: QuantityDerivation | null;
  id?: string;
  name: string;
  description: string;
  category: string;
  quantity: string;
  unit: string;
  unitCost: string;
  section: string;
  // Presentation metadata must round-trip through ordinary edits so saving a
  // price/description change does not reset the item's quote placement.
  displayStage?: string | null;
  displaySubGroup?: string | null;
  sortOrder?: number | null;
  // Multi-scope quotes (Task #680): which detected scope this line belongs to
  // ("__shared__" for de-duplicated shared costs). Absent on single-scope quotes.
  scopeName?: string | null;
  inclusions: string[];
  exclusions: string[];
  labourCost: string;
  materialCost: string;
  plantCost: string;
  _splitBasis?: QuotedCostLine;
  labourHours: string;
  isOptional: boolean;
}

// Sentinel scopeName used for shared site-cost line items in a multi-scope quote.
const SHARED_SCOPE_NAME = "__shared__";


function shortenScopeLabel(label: string): string {
  const floorLabel = floorScopeLabel(label);
  if (floorLabel) return floorLabel;
  const l = label.toLowerCase();
  if (l === "combined") return "Combined";
  if (l.includes("shared")) return "Shared";
  if (l.includes("porch")) return "Porch";
  if (l.includes("extension")) return "Extension";
  if (l.includes("loft")) return "Loft";
  if (l.includes("renovation") || l.includes("refurb")) return "Renovation";
  if (l.includes("kitchen")) return "Kitchen";
  if (l.includes("bathroom")) return "Bathroom";
  if (l.includes("garage")) return "Garage";
  if (l.includes("new build")) return "New Build";
  if (l.includes("roof")) return "Roofing";
  if (l.includes("structural") || l.includes("opening")) return "Structural";
  const first = label.split(/\s+/)[0] || label;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

function toEditable(item: QuoteLineItem): EditableLineItem {
  return {
    materialAllowanceApproval: item.materialAllowanceApproval,
    quoteCostRecipe: item.quoteCostRecipe ? costRecipeSchema.parse(item.quoteCostRecipe) : null,
    quantityDerivation: item.quantityDerivation as QuantityDerivation | null,
    id: item.id,
    name: item.name,
    description: item.description || "",
    category: item.category,
    quantity: String(item.quantity),
    unit: item.unit,
    unitCost: (Math.round(Number(item.unitCost) * 100) / 100).toFixed(2),
    section: (item as any).section || "MAIN WORKS",
    displayStage: item.displayStage ?? null,
    displaySubGroup: item.displaySubGroup ?? null,
    sortOrder: item.sortOrder ?? null,
    scopeName: (item as any).scopeName ?? null,
    inclusions: (item as any).inclusions || [],
    exclusions: (item as any).exclusions || [],
    labourCost: String(item.labourCost || 0),
    materialCost: String(item.materialCost || 0),
    plantCost: String(item.plantCost || 0),
    labourHours: String(item.labourHours || 0),
    isOptional: (item as any).isOptional || false,
  };
}

function newBlankItem(): EditableLineItem {
  return {
    name: "",
    description: "",
    category: "other",
    quantity: "1",
    unit: "item",
    unitCost: "0",
    section: "MAIN WORKS",
    scopeName: null,
    inclusions: [],
    exclusions: [],
    labourCost: "0",
    materialCost: "0",
    plantCost: "0",
    labourHours: "0",
    isOptional: false,
  };
}

// Matches the text appended by applyStructuralEstimateDisclaimer on the server.
// Used to strip the disclaimer from displayed descriptions when seDesignConfirmed=true.
const STRUCTURAL_DISCLAIMER =
  "Structural steel quantities are estimated based on the available architectural drawings. " +
  "Final beam requirements, sizes, and specifications are subject to structural design and may vary " +
  "once structural calculations have been completed.";

export default function QuoteDetailPage() {
  usePageTracking("Quote Detail");
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const market = user?.market || "uk";
  const isAU = market === "au";
  const isCA = market === "ca";
  const isUS = market === "us";
  // Layout flag — AU/CA/US all use the localised currency-symbol layout (A$/C$/$).
  const isLocalised = isAU || isCA || isUS;

  const { data: quote, isLoading } = useQuery<QuoteWithDetails>({
    queryKey: ["/api/quotes", id],
    enabled: !!id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 5000,
  });

  const { data: profile } = useQuery<BuilderProfile>({
    queryKey: ["/api/profile"],
  });

  const showCompanyNameMutation = useMutation({
    mutationFn: async (showCompanyName: boolean) => {
      if (!profile) throw new Error("Company profile is not available");
      const response = await apiRequest("PUT", "/api/profile", {
        companyName: profile.companyName || "My Building Company",
        showCompanyName,
      });
      return response.json() as Promise<BuilderProfile>;
    },
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(["/api/profile"], updatedProfile);
    },
    onError: () => {
      toast({
        title: "Couldn’t update company-name display",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });

  const displayLineItems = useMemo(() =>
    (quote?.lineItems || []).map(withQuotedCostSplit).sort((a, b) => {
      const stageOrder = ["Preparation", "Demolition", "Substructure", "Structure", "Roofing", "External Works", "Finishes", "Services", "Other"];
      const rank = (item: any) => {
        const index = stageOrder.indexOf(getEffectiveStage(item));
        return index < 0 ? stageOrder.length : index;
      };
      const prioA = rank(a);
      const prioB = rank(b);
      if (prioA !== prioB) return prioA - prioB;
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    }),
    [quote?.lineItems]
  );

  // Multi-scope quotes (Task #680): the ordered list of real scope names on the
  // quote. Only a multi-scope quote (2+ scopes) renders the scope tab bar; a
  // single-scope quote behaves exactly as before (no tabs).
  const scopeNames = useMemo<string[]>(() => {
    const declared = ((quote as any)?.scopeNames as string[] | undefined) ?? [];
    return declared.filter(n => n && n !== SHARED_SCOPE_NAME);
  }, [quote]);
  const isMultiScopeQuote = !!(quote as any)?.isMultiScope && scopeNames.length > 1;

  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareTab, setShareTab] = useState<"builder" | "customer">("customer");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerMessage, setCustomerMessage] = useState("");
  const [isWordExporting, setIsWordExporting] = useState(false);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editItems, setEditItems] = useState<EditableLineItem[]>([]);
  // Multi-scope quotes (Task #680): active scope tab ("__combined__" = merged
  // view). Only rendered when the saved quote spans 2+ scopes.
  const [activeScopeTab, setActiveScopeTab] = useState<string>("__combined__");

  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState({ clientId: "", clientName: "", projectAddress: "", projectType: "", validUntil: "" });
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const clientComboRef = useRef<HTMLDivElement>(null);

  const enterEditMode = useCallback(() => {
    setEditItems(displayLineItems.map(toEditable));
    setIsEditMode(true);
  }, [displayLineItems]);

  const discardEdits = useCallback(() => {
    setIsEditMode(false);
    setEditItems([]);
  }, []);

  const updateItem = useCallback((index: number, field: keyof EditableLineItem, value: string | boolean) => {
    setEditItems(prev => {
      const updated = [...prev];
      const original = updated[index];
      const next = { ...original, [field]: value };
      if (field === "quantity" || field === "unitCost") {
        updated[index] = applyQuotedPriceEdit(original, next);
        if (next.quantityDerivation && field === "quantity")
          updated[index].quantityDerivation = { ...next.quantityDerivation, quantity_overridden: true };
        if (next.quoteCostRecipe) {
          try {
            const recipe = field === "unitCost" ? recipeAtQuotePrice(next.quoteCostRecipe, Number(next.unitCost)) : next.quoteCostRecipe;
            const split = quoteRecipeSplit(recipe, Number(next.quantity));
            updated[index] = { ...updated[index], quoteCostRecipe: recipe, labourCost: String(split.labourCost), materialCost: String(split.materialCost), plantCost: String(split.plantCost) };
          } catch { updated[index].quoteCostRecipe = null; }
        }
      } else {
        updated[index] = next;
        if (field === "unit" && next.unit !== original.unit) {
          updated[index].quoteCostRecipe = null;
          if (next.quantityDerivation) updated[index].quantityDerivation = { ...next.quantityDerivation, quantity_overridden: true };
        }
      }
      return updated;
    });
  }, []);

  const removeItem = useCallback((index: number) => {
    setEditItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const addItem = useCallback(() => {
    setEditItems(prev => [...prev, newBlankItem()]);
  }, []);

  const liveSubtotal = useMemo(() =>
    editItems.filter(item => !item.isOptional && (
      !isMultiScopeQuote || activeScopeTab === "__combined__" ||
      item.scopeName === activeScopeTab || item.scopeName === SHARED_SCOPE_NAME
    )).reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const cost = parseFloat(item.unitCost) || 0;
      return sum + quotedMoney(qty * cost);
    }, 0),
    [editItems, isMultiScopeQuote, activeScopeTab]
  );

  const liveTotals = useMemo(() => {
    if (!quote) return { contingency: 0, overhead: 0, profit: 0, net: liveSubtotal, vat: 0, grand: liveSubtotal };
    const storedSubtotal = Number(quote.subtotal || 0);
    const scale = (amount: unknown) =>
      storedSubtotal > 0 ? liveSubtotal * (Number(amount || 0) / storedSubtotal) : 0;
    const contingency = scale(quote.contingencyAmount);
    const overhead = scale(quote.overheadAmount);
    const profit = scale(quote.profitAmount);
    const net = liveSubtotal + contingency + overhead + profit;
    const defaultRate = isAU ? 10 : (isCA || isUS) ? Number((user as any)?.customTaxRate || 0) : 20;
    const vatRate = Number(quote.vatRate || defaultRate);
    const vat = quote.includeVat ? net * (vatRate / 100) : 0;
    return { contingency, overhead, profit, net, vat, grand: net + vat };
  }, [editItems, quote, liveSubtotal, isAU, isCA, isUS, user]);

  const updateTitleMutation = useMutation({
    mutationFn: (quoteNumber: string) => apiRequest("PATCH", `/api/quotes/${id}/title`, { quoteNumber }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      setIsEditingTitle(false);
      toast({ title: "Quote title updated" });
    },
    onError: () => {
      toast({ title: "Failed to update title", variant: "destructive" });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: string) => apiRequest("PATCH", `/api/quotes/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes/recent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      toast({ title: "Quote status updated" });
    },
    onError: () => {
      toast({ title: "Failed to update status", variant: "destructive" });
    },
  });

  const duplicateQuoteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/quotes/${id}/duplicate`, {});
      return response.json() as Promise<{ id: string }>;
    },
    onSuccess: (copy) => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes/recent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      toast({ title: "Quote duplicated", description: "Your new copy is ready to edit. The original is unchanged." });
      navigate(`/quotes/${copy.id}`);
    },
    onError: (error: Error) => {
      toast({ title: "Could not duplicate quote", description: error.message, variant: "destructive" });
    },
  });

  // PATCH /api/quotes/:id/settings — toggles for customer-facing display.
  const updateSettingsMutation = useMutation({
    mutationFn: (settings: { showCostBreakdown?: boolean; showItemDescriptions?: boolean; showQuantityUnits?: boolean; showLineItems?: boolean; showMarkupBreakdown?: boolean; includeVat?: boolean; seDesignConfirmed?: boolean; summaryMode?: boolean }) =>
      apiRequest("PATCH", `/api/quotes/${id}/settings`, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
    },
    onError: () => {
      toast({ title: "Failed to update settings", variant: "destructive" });
    },
  });

  const updateDetailsMutation = useMutation({
    mutationFn: (data: { clientId?: string | null; projectAddress?: string | null; projectType?: string | null; validUntil?: string | null }) =>
      apiRequest("PATCH", `/api/quotes/${id}/details`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      setIsEditingDetails(false);
      toast({ title: "Project details updated" });
    },
    onError: () => {
      toast({ title: "Failed to update project details", variant: "destructive" });
    },
  });

  const [isSavingDetails, setIsSavingDetails] = useState(false);

  const handleSaveDetails = useCallback(async () => {
    setIsSavingDetails(true);
    try {
      let resolvedClientId: string | null = detailsForm.clientId || null;
      if (detailsForm.clientName.trim() && !detailsForm.clientId) {
        const newClient = await apiRequest("POST", "/api/clients", { name: detailsForm.clientName.trim() }).then(r => r.json()) as Client;
        resolvedClientId = newClient.id;
        queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      }
      if (!detailsForm.clientName.trim()) resolvedClientId = null;
      await apiRequest("PATCH", `/api/quotes/${id}/details`, {
        clientId: resolvedClientId,
        projectAddress: detailsForm.projectAddress || null,
        projectType: detailsForm.projectType || null,
        validUntil: detailsForm.validUntil || null,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      setIsEditingDetails(false);
      toast({ title: "Project details updated" });
    } catch {
      toast({ title: "Failed to update project details", variant: "destructive" });
    } finally {
      setIsSavingDetails(false);
    }
  }, [detailsForm, id]);

  const saveEditsMutation = useMutation({
    mutationFn: async (items: EditableLineItem[]) => {
      const response = await apiRequest("PUT", `/api/quotes/${id}/line-items`, {
        lineItems: items.map(({ _splitBasis, ...item }) => item),
      });
      return response.json();
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(["/api/quotes", id], saved);
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes/recent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      setIsEditMode(false);
      setEditItems([]);
      toast({ title: "Quote updated", description: "All changes saved successfully." });
    },
    onError: () => {
      toast({ title: "Failed to save changes", variant: "destructive" });
    },
  });

  const { data: clients = [] } = useQuery<Client[]>({ queryKey: ["/api/clients"] });

  const { data: outgoingShares = [] } = useQuery<{ id: string; recipientEmail: string; status: string; createdAt: string; recipientKind?: string }[]>({
    queryKey: ["/api/quotes", id, "shares"],
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 5000,
    enabled: !!id && !isEditMode,
  });

  const shareMutation = useMutation({
    mutationFn: ({ email }: { email: string }) =>
      apiRequest("POST", `/api/quotes/${id}/share`, { email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", id, "shares"] });
      setIsShareDialogOpen(false);
      setShareEmail("");
      toast({ title: "Quote shared", description: "The quote has been added directly to their account." });
    },
    onError: (err: any) => {
      toast({ title: err.message || "Failed to share quote", variant: "destructive" });
    },
  });

  const sendToCustomerMutation = useMutation({
    mutationFn: async ({ email, message }: { email: string; message: string }) => {
      if (!quote) throw new Error("Quote not loaded");
      const { quoteData, company } = getExportInputs();
      const pdfBase64 = await generateQuotePDF(quoteData, company, { returnBase64: true });

      return apiRequest("POST", `/api/quotes/${id}/send-to-customer`, {
        email,
        message,
        pdfBase64,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", id, "shares"] });
      setIsShareDialogOpen(false);
      setCustomerEmail("");
      setCustomerMessage("");
      const mode = getBuilderQuoteMode(quote?.quoteType);
      if (mode) {
        trackBuilderEvent("quote_sent", { product: "builder", mode });
      }
      toast({ title: "Quote sent to customer", description: "The PDF has been emailed and they can view it online." });
    },
    onError: (err: any) => {
      toast({ title: err.message || "Failed to send quote", variant: "destructive" });
    },
  });

  const acceptSharedMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/quotes/${id}/accept-shared`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/shares/pending-count"] });
      toast({ title: "Quote accepted", description: "The quote has been added to your account." });
    },
    onError: (err: any) => {
      toast({ title: err.message || "Failed to accept quote", variant: "destructive" });
    },
  });

  const declineSharedMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/quotes/${id}/decline-shared`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/shares/pending-count"] });
      toast({ title: "Quote declined", description: "The shared quote has been removed." });
      navigate("/quotes");
    },
    onError: (err: any) => {
      toast({ title: err.message || "Failed to decline quote", variant: "destructive" });
    },
  });

  // Delegate to the shared `formatMoney` helper so CA gets the C$ post-process
  // (en-CA + CAD natively renders bare "$" which is ambiguous next to USD).
  // UK and AU paths return the same Intl.NumberFormat output as before.
  const formatCurrency = (value: number | string) => formatMoney(value, market);

  const COST_CATEGORIES = getCostCategories(market);
  const getCategoryLabel = (value: string) =>
    COST_CATEGORIES.find(c => c.value === value)?.label || value;

  const getUnitLabel = (value: string) =>
    (UNITS as Record<string, string>)[value] || value;

  if (isLoading) {
    return <div className="p-6 max-w-5xl mx-auto"><FormSkeleton /></div>;
  }

  if (!quote) {
    return (
      <div className="p-6 max-w-5xl mx-auto text-center">
        <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-xl font-bold mb-2">Quote not found</h2>
        <p className="text-muted-foreground mb-4">The quote you're looking for doesn't exist.</p>
        <Button asChild><Link href="/quotes">Back to Quotes</Link></Button>
      </div>
    );
  }

  const learnedAdjustments = quote.learnedAdjustments || [];

  // Multi-scope quotes (Task #680): filter the rendered rows by the active scope
  // tab. Combined view (or any single-scope quote) shows every row unchanged.
  // Individual scope tabs also surface shared site-cost items (prelims, scaffold,
  // skip hire etc.) so each scope view is complete.
  const scopeFilter = (sn: string | null | undefined): boolean => {
    if (!isMultiScopeQuote || activeScopeTab === "__combined__") return true;
    const scopeName = sn ?? null;
    // Show items for this scope plus shared site-cost items.
    return scopeName === activeScopeTab || scopeName === SHARED_SCOPE_NAME;
  };
  // Preserve each edit row's original index so updateItem/removeItem still target
  // the correct entry in the unfiltered editItems array.
  const visibleEditItems = editItems
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => scopeFilter(item.scopeName));
  const visibleDisplayItemsRaw = displayLineItems.filter(it => scopeFilter((it as any).scopeName));
  // In combined view (display mode only), merge duplicate-named items so each
  // appears once with summed quantities and costs — keeps the combined view clean.
  const visibleDisplayItems: QuoteLineItem[] = (() => {
    if (!isMultiScopeQuote || activeScopeTab !== "__combined__") return visibleDisplayItemsRaw;
    // Equivalence-based grouping: merges the same work type across scopes even
    // when names differ slightly ("Waste Removal Skips" + "Porch Waste Skip").
    // Core and optional items are grouped separately so cost never crosses the
    // quoted-price boundary.
    const mergeBucket = (bucket: QuoteLineItem[]) => groupEquivalentLineItems(
      bucket.map(item => ({
        name: item.name || "",
        category: item.category || "",
        unit: item.unit || "",
        unitCost: Number(item.unitCost),
      })),
    ).map(g => ({ ...g, bucket }));
    const groups = [
      ...mergeBucket(visibleDisplayItemsRaw.filter(it => !(it as any).isOptional)),
      ...mergeBucket(visibleDisplayItemsRaw.filter(it => !!(it as any).isOptional)),
    ];
    return groups.map(({ indices, displayName, bucket }) => {
      const members = indices.map(i => bucket[i]) as any[];
      const first = members[0];
      if (members.length === 1) return { ...first, name: displayName };
      const mergedTotal = members.reduce((s, m) => s + Number(m.subtotal ?? quotedMoney(Number(m.quantity) * Number(m.unitCost))), 0);
      const isBundled = (first.unit || "").toLowerCase() === "item";
      const mergedQty = isBundled ? 1 : members.reduce((s, m) => s + Number(m.quantity), 0);
      return {
        ...first,
        name: displayName,
        quantity: mergedQty,
        unitCost: isBundled ? mergedTotal : (mergedQty > 0 ? mergedTotal / mergedQty : Number(first.unitCost)),
        subtotal: mergedTotal,
        labourCost: members.reduce((s, m) => s + Number(m.labourCost ?? 0), 0),
        materialCost: members.reduce((s, m) => s + Number(m.materialCost ?? 0), 0),
        plantCost: members.reduce((s, m) => s + Number(m.plantCost ?? 0), 0),
      };
    });
  })();
  const activeItems = isEditMode ? visibleEditItems : visibleDisplayItems;


  const scopeTabLabel = (sn: string): string =>
    sn === SHARED_SCOPE_NAME ? "Shared site costs" : sn;

  const getExportInputs = () => buildQuoteExportInputs({
    quote,
    profile,
    market,
    isMultiScope: isMultiScopeQuote,
    activeScope: activeScopeTab,
    scopeNames,
  });
  const scopedQuoteTotals = getExportInputs().quoteData;
  // Use raw persisted rows, not combined-view display groups: one source row
  // contributes once, including shared costs when a single scope is selected.
  const costTotals = calculateQuoteCostTotals(
    isEditMode ? visibleEditItems.map(({ item }) => item) : visibleDisplayItemsRaw,
    Number(isEditMode ? liveSubtotal : scopedQuoteTotals.subtotal),
  );

  const handleWordDownload = async () => {
    if (isWordExporting) return;
    setIsWordExporting(true);
    try {
      const { quoteData, company } = getExportInputs();
      const { generateQuoteWord } = await import("@/lib/word-generator");
      await generateQuoteWord(quoteData, company);
      const mode = getBuilderQuoteMode(quote.quoteType);
      if (mode) trackBuilderEvent("quote_exported", { product: "builder", mode });
    } catch (error) {
      toast({
        title: "Failed to download Word document",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsWordExporting(false);
    }
  };

  const seDesignConfirmed = !!(quote as any).seDesignConfirmed;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-2">
          {profile && (
            <>
              <BrandingDisplay
                logoUrl={profile.logoUrl}
                companyName={profile.companyName || "Your company"}
                showCompanyName={profile.showCompanyName ?? true}
                logoSize={
                  profile.logoSize === "small" || profile.logoSize === "large"
                    ? profile.logoSize
                    : "medium"
                }
                className="flex flex-col items-start gap-1"
                nameClassName="font-semibold text-foreground"
                imageTestId="img-quote-logo"
                nameTestId="text-quote-company"
              />
              <div className="flex items-center gap-2">
                <Switch
                  id="quote-show-company-name"
                  size="sm"
                  checked={profile.showCompanyName ?? true}
                  onCheckedChange={(checked) => showCompanyNameMutation.mutate(checked)}
                  disabled={showCompanyNameMutation.isPending}
                  data-testid="switch-quote-show-company-name"
                />
                <Label htmlFor="quote-show-company-name" className="text-xs font-normal text-muted-foreground">
                  Show company name
                </Label>
              </div>
            </>
          )}
          <div className="flex items-center gap-4 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/quotes")} data-testid="button-back" className="shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {isEditingTitle ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="text-xl font-bold h-9 w-48"
                    data-testid="input-edit-title"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") updateTitleMutation.mutate(editTitle);
                      if (e.key === "Escape") setIsEditingTitle(false);
                    }}
                  />
                  <Button size="icon" variant="ghost" onClick={() => updateTitleMutation.mutate(editTitle)} disabled={updateTitleMutation.isPending} data-testid="button-save-title">
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setIsEditingTitle(false)} data-testid="button-cancel-title">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h1 className="text-lg sm:text-2xl font-bold" data-testid="text-quote-number">{quote.quoteNumber}</h1>
                  <Button size="icon" variant="ghost" onClick={() => { setEditTitle(quote.quoteNumber); setIsEditingTitle(true); }} data-testid="button-edit-title">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
              <StatusBadge status={quote.status || "draft"} />
            </div>
          </div>
          </div>
        </div>
      </div>

      {!isEditMode && quote.status !== "shared" && (
        <PdfTemplateQuoteControls id={id} currentVersion={(quote as any).pdfTemplate?.version ?? null} />
      )}

        <div className="flex gap-1.5 flex-wrap items-center" data-testid="quote-action-toolbar">
          {quote.status === "shared" && (
            <>
              <Button
                onClick={() => acceptSharedMutation.mutate()}
                disabled={acceptSharedMutation.isPending || declineSharedMutation.isPending}
                data-testid="button-accept-shared"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {acceptSharedMutation.isPending ? "Accepting..." : "Accept Quote"}
              </Button>
              <Button
                variant="outline"
                onClick={() => declineSharedMutation.mutate()}
                disabled={acceptSharedMutation.isPending || declineSharedMutation.isPending}
                className="text-destructive border-destructive/40 hover:bg-destructive/10"
                data-testid="button-decline-shared"
              >
                <X className="h-4 w-4 mr-2" />
                {declineSharedMutation.isPending ? "Declining..." : "Decline"}
              </Button>
            </>
          )}
          {!isEditMode && quote.status !== "shared" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsShareDialogOpen(true)}
              data-testid="button-share-quote"
            >
              <Share2 className="h-3.5 w-3.5 mr-1" />
              <span className="text-xs">Share</span>
            </Button>
          )}
          {!isEditMode && quote.status !== "shared" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => duplicateQuoteMutation.mutate()}
              disabled={duplicateQuoteMutation.isPending}
              data-testid="button-duplicate-quote"
            >
              {duplicateQuoteMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
              <span className="text-xs">{duplicateQuoteMutation.isPending ? "Copying..." : "Duplicate"}</span>
            </Button>
          )}
          {!isEditMode && quote.status !== "shared" && (
            <PdfTemplateDesignPrompt currentVersion={(quote as any).pdfTemplate?.version ?? null} />
          )}
          {!isEditMode && (
            <Button
              variant="outline"
              size="sm"
              data-testid="button-download-pdf"
              onClick={async () => {
                trackEvent("pdf_downloaded", window.location.pathname, { quoteNumber: quote.quoteNumber });
                const { quoteData, company } = getExportInputs();
                await generateQuotePDF(quoteData, company);
                const mode = getBuilderQuoteMode(quote.quoteType);
                if (mode) {
                  trackBuilderEvent("quote_exported", { product: "builder", mode });
                }
              }}
            >
              <span
                aria-hidden="true"
                className="mr-1 inline-flex h-4 min-w-4 items-center justify-center rounded-sm bg-red-600 px-0.5 text-[7px] font-bold leading-none text-white"
              >
                PDF
              </span>
              <span className="text-xs">Download</span>
              <span className="sr-only"> PDF</span>
            </Button>
          )}
          {!isEditMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleWordDownload}
              disabled={isWordExporting}
              data-testid="button-download-word"
            >
              {isWordExporting ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <span
                  aria-hidden="true"
                  className="mr-1 inline-flex h-4 w-4 items-center justify-center rounded-sm bg-blue-600 text-[10px] font-bold leading-none text-white"
                >
                  W
                </span>
              )}
              <span className="text-xs">{isWordExporting ? "Preparing..." : "Download"}</span>
              <span className="sr-only"> Word</span>
            </Button>
          )}
          {!isEditMode && quote.status !== "shared" && (
            <QuoteStatusControl
              status={quote.status || "draft"}
              onChange={(status) => updateStatusMutation.mutate(status)}
              disabled={updateStatusMutation.isPending}
            />
          )}
        </div>
      {/* Learned Adjustments Banner */}
      {learnedAdjustments.length > 0 && !isEditMode && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex items-start gap-3">
          <TrendingUp className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <p className="font-medium text-foreground">This quote includes adjustments</p>
            <p className="text-sm text-muted-foreground">{learnedAdjustments.join(", ")}</p>
          </div>
        </div>
      )}

      {/* Project Info + Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Project Details</CardTitle>
            {!isEditingDetails && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setDetailsForm({
                    clientId: quote.clientId ?? "",
                    clientName: quote.client?.name ?? "",
                    projectAddress: quote.projectAddress ?? "",
                    projectType: quote.projectType ?? "",
                    validUntil: quote.validUntil ? new Date(quote.validUntil).toISOString().split("T")[0] : "",
                  });
                  setClientDropdownOpen(false);
                  setIsEditingDetails(true);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditingDetails ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Client</Label>
                    <div className="relative" ref={clientComboRef}>
                      <Input
                        value={detailsForm.clientName}
                        onChange={(e) => {
                          setDetailsForm(f => ({ ...f, clientName: e.target.value, clientId: "" }));
                          setClientDropdownOpen(true);
                        }}
                        onFocus={() => setClientDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setClientDropdownOpen(false), 100)}
                        placeholder="Search or type new name…"
                        className="h-9"
                        autoComplete="off"
                      />
                      {clientDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-52 overflow-y-auto">
                          <div
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-accent text-muted-foreground italic"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setDetailsForm(f => ({ ...f, clientId: "", clientName: "" }));
                              setClientDropdownOpen(false);
                            }}
                          >
                            No client
                          </div>
                          {clients
                            .filter(c => !detailsForm.clientName || c.name.toLowerCase().includes(detailsForm.clientName.toLowerCase()))
                            .map(c => (
                              <div
                                key={c.id}
                                className="px-3 py-2 text-sm cursor-pointer hover:bg-accent flex items-center justify-between"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setDetailsForm(f => ({ ...f, clientId: c.id, clientName: c.name }));
                                  setClientDropdownOpen(false);
                                }}
                              >
                                {c.name}
                                {detailsForm.clientId === c.id && <Check className="h-3.5 w-3.5 text-primary" />}
                              </div>
                            ))
                          }
                          {detailsForm.clientName.trim() && !clients.some(c => c.name.toLowerCase() === detailsForm.clientName.toLowerCase()) && (
                            <div
                              className="px-3 py-2 text-sm cursor-pointer hover:bg-accent text-primary font-medium border-t"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setDetailsForm(f => ({ ...f, clientId: "" }));
                                setClientDropdownOpen(false);
                              }}
                            >
                              Create "{detailsForm.clientName.trim()}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Project Type</Label>
                    <Select
                      value={detailsForm.projectType || "__none__"}
                      onValueChange={(v) => setDetailsForm(f => ({ ...f, projectType: v === "__none__" ? "" : v }))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select type…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Not specified</SelectItem>
                        {getProjectTypes(user?.market).map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="text-xs text-muted-foreground">Project Address</Label>
                    <Input
                      value={detailsForm.projectAddress}
                      onChange={(e) => setDetailsForm(f => ({ ...f, projectAddress: e.target.value }))}
                      placeholder="e.g. 12 Oak Street, London"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Valid Until</Label>
                    <Input
                      type="date"
                      value={detailsForm.validUntil}
                      onChange={(e) => setDetailsForm(f => ({ ...f, validUntil: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    onClick={handleSaveDetails}
                    disabled={isSavingDetails}
                  >
                    <Check className="h-3.5 w-3.5 mr-1" />
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditingDetails(false)}
                    disabled={isSavingDetails}
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <div className="text-sm text-muted-foreground">Client</div>
                    <div className="font-medium">{quote.client?.name ?? <span className="text-muted-foreground italic">Not set</span>}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <div className="text-sm text-muted-foreground">Project Type</div>
                    <div className="font-medium">
                      {quote.projectType
                        ? quote.projectType.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
                        : <span className="text-muted-foreground italic">Not set</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <div className="text-sm text-muted-foreground">Project Address</div>
                    <div className="font-medium">{quote.projectAddress ?? <span className="text-muted-foreground italic">Not set</span>}</div>
                  </div>
                </div>
                {!isEditMode && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div>
                      <div className="text-sm text-muted-foreground">Valid Until</div>
                      <div className="font-medium">
                        {quote.validUntil
                          ? new Date(quote.validUntil).toLocaleDateString("en-GB")
                          : <span className="text-muted-foreground italic">Not set</span>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quote Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatCurrency(isEditMode ? liveSubtotal : (scopedQuoteTotals.subtotal || 0))}</span>
            </div>
            <div className="rounded-md border bg-muted/30 p-3 space-y-2" data-testid="builder-cost-totals">
              <div className="text-xs font-semibold text-foreground">Cost split check · builder only</div>
              <p className="text-xs text-muted-foreground">
                Non-optional line items before overhead, profit and tax. Not added to the subtotal.
              </p>
              <div className="flex justify-between text-sm"><span>Labour</span><span data-testid="total-labour">{formatCurrency(costTotals.labour)}</span></div>
              <div className="flex justify-between text-sm"><span>Materials</span><span data-testid="total-materials">{formatCurrency(costTotals.materials)}</span></div>
              <div className="flex justify-between text-sm"><span>Plant</span><span data-testid="total-plant">{formatCurrency(costTotals.plant)}</span></div>
              {isEditMode && (
                <p className="text-xs text-muted-foreground">Live estimate — changed prices and quantities scale existing splits; saving may recalculate them.</p>
              )}
            </div>
            {(quote as any).showMarkupBreakdown !== false && (
              <>
                {Number(isEditMode ? liveTotals.contingency : scopedQuoteTotals.contingencyAmount) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Contingency</span>
                    <span>{formatCurrency(isEditMode ? liveTotals.contingency : (scopedQuoteTotals.contingencyAmount || 0))}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Overhead</span>
                  <span>{formatCurrency(isEditMode ? liveTotals.overhead : (scopedQuoteTotals.overheadAmount || 0))}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Profit</span>
                  <span>{formatCurrency(isEditMode ? liveTotals.profit : (scopedQuoteTotals.profitAmount || 0))}</span>
                </div>
              </>
            )}
            <div className="flex justify-between text-sm border-t pt-2">
              <span className="text-muted-foreground">Net Total</span>
              <span>{formatCurrency(isEditMode ? liveTotals.net : (scopedQuoteTotals.totalPrice || 0))}</span>
            </div>
            {quote.includeVat && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{isAU ? "GST" : (isCA || isUS) ? "Sales Tax" : "VAT"} ({quote.vatRate}%)</span>
                <span>{formatCurrency(isEditMode ? liveTotals.vat : (scopedQuoteTotals.vatAmount || 0))}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold border-t pt-3">
              <span>Grand Total</span>
              <span className={`text-primary ${isEditMode ? "text-amber-600 dark:text-amber-400" : ""}`} data-testid="text-grand-total">
                {formatCurrency(isEditMode ? liveTotals.grand : (scopedQuoteTotals.grandTotal || 0))}
              </span>
            </div>
            {isEditMode && (
              <p className="text-xs text-muted-foreground">Live preview — overhead and profit recalculated on save</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Scope of Works */}
      <Card className={isEditMode ? "ring-2 ring-primary/30" : ""}>
        <CardHeader className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-base">Scope of Works</CardTitle>
              {!isEditMode && <CardDescription>Detailed breakdown of all items in this quote.</CardDescription>}
              {isEditMode && <CardDescription className="text-amber-600 dark:text-amber-400">Editing mode — click any field to update it.</CardDescription>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isEditMode ? (
                <>
                  <Button variant="outline" size="sm" onClick={discardEdits} disabled={saveEditsMutation.isPending} data-testid="button-discard-edits">
                    <X className="h-3.5 w-3.5 mr-1.5" />
                    Discard
                  </Button>
                  <Button size="sm" onClick={() => saveEditsMutation.mutate(editItems)} disabled={saveEditsMutation.isPending} data-testid="button-save-edits">
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                    {saveEditsMutation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => navigate(`/quotes/${id}/edit`)} data-testid="button-edit-items">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Edit Items
                </Button>
              )}
            </div>
          </div>
          {!isEditMode && (
            // Display toggles. These control what appears in this preview AND
            // in the downloaded PDF — what you see is what the customer gets.
            // Mobile: column-major 2-col grid (col 1: Summary/Total Only/Description,
            // col 2: Breakdown/Qty and Unit/Markup); desktop: inline flex row.
            <div className="grid grid-flow-col grid-rows-3 grid-cols-2 gap-x-4 gap-y-2 sm:flex sm:flex-wrap sm:items-center sm:gap-x-6 pt-1 border-t border-border/60">
              <div className="flex items-center gap-2">
                <Switch
                  id="summary-mode"
                  checked={!!(quote as any).summaryMode}
                  disabled={updateSettingsMutation.isPending}
                  onCheckedChange={(checked) => updateSettingsMutation.mutate(
                    checked
                      // Summary view replaces the detailed item list, so switch the
                      // other display toggles off — except Markup, which the user
                      // controls independently.
                      ? { summaryMode: true, showLineItems: true, showCostBreakdown: false, showItemDescriptions: false, showQuantityUnits: false }
                      : { summaryMode: false }
                  )}
                  data-testid="switch-summary-mode"
                />
                <Label htmlFor="summary-mode" className="text-xs text-muted-foreground cursor-pointer">
                  Summary
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="total-price-only"
                  checked={(quote as any).showLineItems === false}
                  disabled={updateSettingsMutation.isPending}
                  onCheckedChange={(checked) => updateSettingsMutation.mutate(
                    checked
                      // Turning this on hides per-item pricing (unit cost, subtotal,
                      // and the cost breakdown), so also switch those off — the item
                      // list and categories themselves stay visible.
                      ? { showLineItems: false, showQuantityUnits: false, showCostBreakdown: false }
                      : { showLineItems: true }
                  )}
                  data-testid="switch-total-price-only"
                />
                <Label htmlFor="total-price-only" className="text-xs text-muted-foreground cursor-pointer">
                  Total Only
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="show-item-descriptions"
                  checked={(quote as any).showItemDescriptions !== false}
                  disabled={updateSettingsMutation.isPending}
                  onCheckedChange={(checked) => updateSettingsMutation.mutate({ showItemDescriptions: checked })}
                  data-testid="switch-show-item-descriptions"
                />
                <Label htmlFor="show-item-descriptions" className="text-xs text-muted-foreground cursor-pointer">
                  Description
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="show-cost-breakdown"
                  checked={!!quote.showCostBreakdown}
                  disabled={updateSettingsMutation.isPending}
                  onCheckedChange={(checked) => updateSettingsMutation.mutate(
                    checked ? { showCostBreakdown: true, showLineItems: true } : { showCostBreakdown: false }
                  )}
                  data-testid="switch-show-cost-breakdown"
                />
                <Label htmlFor="show-cost-breakdown" className="text-xs text-muted-foreground cursor-pointer">
                  Breakdown
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="show-quantity-units"
                  checked={(quote as any).showQuantityUnits !== false}
                  disabled={updateSettingsMutation.isPending}
                  onCheckedChange={(checked) => updateSettingsMutation.mutate(
                    checked ? { showQuantityUnits: true, showLineItems: true } : { showQuantityUnits: false }
                  )}
                  data-testid="switch-show-quantity-units"
                />
                <Label htmlFor="show-quantity-units" className="text-xs text-muted-foreground cursor-pointer">
                  Qty and Unit
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="show-markup-breakdown"
                  checked={(quote as any).showMarkupBreakdown !== false}
                  disabled={updateSettingsMutation.isPending}
                  onCheckedChange={(checked) => updateSettingsMutation.mutate({ showMarkupBreakdown: checked })}
                  data-testid="switch-show-markup-breakdown"
                />
                <Label htmlFor="show-markup-breakdown" className="text-xs text-muted-foreground cursor-pointer">
                  Markup
                </Label>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {activeItems.length === 0 && !isEditMode ? (
            <p className="text-muted-foreground text-center py-8">No line items in this quote</p>
          ) : (() => {
            // Display flags. In edit mode the builder needs every column to be
            // editable, so the toggles are ignored and we show everything.
            const showDescriptionsView = isEditMode || (quote as any).showItemDescriptions !== false;
            const showQtyUnitsView = isEditMode || (quote as any).showQuantityUnits !== false;
            const showBreakdownView = isEditMode || !!quote.showCostBreakdown;
            // Total Price Only hides the per-item Subtotal column too (not just
            // Qty/Unit/Unit Cost) — this preview mirrors the public page/PDF,
            // where only the overall Subtotal/VAT/Grand Total summary shows.
            const showSubtotalColumn = isEditMode || (quote as any).showLineItems !== false;
            // colSpan for the breakdown row covers exactly the visible columns
            // so the right-aligned content sits under Subtotal correctly.
            const visibleColCount = 2 /* Item + Category */ + (showQtyUnitsView ? 3 : 0) + (showSubtotalColumn ? 1 : 0);
            return (
            <>
            {/* Multi-scope quotes (Task #680): scope tabs. Only shown when the
                saved quote spans 2+ distinct scopes. Single-scope quotes render
                the list exactly as before (no tab bar). */}
            {isMultiScopeQuote && (() => {
              const sym = getMarketCurrency(user?.market).symbol;
              const scopeTotal = (scopeKey: string) =>
                displayLineItems
                  .filter(li => {
                    if ((li as any).isOptional) return false;
                    const itemScope = (li as any).scopeName ?? null;
                    return scopeKey === "__combined__" || itemScope === scopeKey || itemScope === SHARED_SCOPE_NAME;
                  })
                  .reduce((sum, li) => sum + Number(li.subtotal || (Number(li.quantity) * Number(li.unitCost))), 0);
              const tabs = [
                { key: "__combined__", label: "Combined" },
                ...scopeNames.map(sn => ({ key: sn, label: sn })),
              ];
              const getScopeIcon = (label: string) => {
                const l = label.toLowerCase();
                if (l === "combined" || l === "all") return LayoutGrid;
                if (l.includes("shared")) return Share2;
                if (l.includes("extension")) return Building2;
                if (l.includes("loft")) return ArrowUpFromLine;
                if (l.includes("renovation") || l.includes("refurb")) return Hammer;
                if (l.includes("kitchen")) return Wrench;
                if (l.includes("bathroom")) return Droplets;
                if (l.includes("garage")) return Car;
                if (l.includes("new build")) return Landmark;
                if (l.includes("roof")) return Home;
                if (l.includes("structural") || l.includes("opening")) return Columns;
                return HardHat;
              };
              return (
                <>
                  {/* Row 1: scope (project) tabs */}
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="scope-tabs">
                    {tabs.map(tab => {
                      const Icon = getScopeIcon(tab.label);
                      const isActive = activeScopeTab === tab.key;
                      const shortLabel = shortenScopeLabel(tab.label);
                      return (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setActiveScopeTab(tab.key)}
                          data-testid={`scope-tab-${tab.key}`}
                          className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg border transition-colors w-[76px] ${
                            isActive
                              ? "bg-primary/10 text-primary border-primary"
                              : "bg-card text-muted-foreground border-border hover:bg-muted/50"
                          }`}
                        >
                          <Icon className="h-5 w-5 shrink-0" />
                          <span className="text-[11px] font-medium text-center leading-tight">{shortLabel}</span>
                          <span className={`text-[11px] font-semibold ${isActive ? "text-primary" : "text-foreground"}`}>
                            {sym}{scopeTotal(tab.key).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              );
            })()}
            {((quote as any)?.summaryMode && !isEditMode) ? (
              /* ── Summary view: one card per category with optional sub-group rows ── */
              (() => {
                const catTotals = new Map<string, number>();
                const catSubGroupMaps = new Map<string, Map<string, number>>();
                const subGroupIdOfLabel = new Map<string, string>();
                for (const item of visibleDisplayItems.filter(it => !(it as any).isOptional)) {
                   const sg = getEffectiveSubGroup(item as any);
                  // Sub-groups have one canonical section — keeps e.g. "Demolition
                  // Works" from appearing under two sections (matches PDF/public view).
                   const section = (item as any).displayStage?.trim()
                     || (sg ? SUB_GROUP_SECTION[sg as ItemSubGroupId] : getCategorySection((item as any).category));
                  const itemTotal = Number((item as any).subtotal) || (Number(item.quantity) * Number(item.unitCost));
                  catTotals.set(section, (catTotals.get(section) ?? 0) + itemTotal);
                  if (sg) {
                     const label = (item as any).displaySubGroup?.trim() || (ITEM_SUB_GROUP_LABELS[sg as ItemSubGroupId] ?? sg);
                    subGroupIdOfLabel.set(label, sg);
                    if (!catSubGroupMaps.has(section)) catSubGroupMaps.set(section, new Map());
                    const sgMap = catSubGroupMaps.get(section)!;
                    sgMap.set(label, (sgMap.get(label) ?? 0) + itemTotal);
                  }
                }
                 const sectionOrder = [...QUOTE_PLACEMENT_STAGE_ORDER, ...Array.from(catTotals.keys()).filter(sec => !QUOTE_PLACEMENT_STAGE_ORDER.includes(sec as any))];
                 const rows = sectionOrder.filter(sec => catTotals.has(sec)).map(sec => ({
                  category: sec as string,
                  total: catTotals.get(sec) ?? 0,
                  subGroups: Array.from(catSubGroupMaps.get(sec)?.entries() ?? []).map(([lbl, tot]) => ({ label: lbl, total: tot, sgId: subGroupIdOfLabel.get(lbl) })),
                }));
                return (
                  <div className="space-y-2 py-2" data-testid="summary-view">
                    {rows.map(({ category, total, subGroups }, idx) => (
                      <div key={idx} className="border rounded-lg overflow-hidden">
                        <div className="flex justify-between items-center px-4 py-3">
                          <span className="font-medium">{quoteHeadingLabel(quote.headingNames, category)}</span>
                          <span className="font-bold text-primary">{formatCurrency(total)}</span>
                        </div>
                        {subGroups.length > 0 && (
                          <div className="border-t divide-y">
                            {subGroups.map(({ label, total: sgTotal, sgId }, sgIdx) => {
                              const SgIcon = sgId ? SUB_GROUP_ICONS[sgId] : undefined;
                              return (
                                <div key={sgIdx} className="flex justify-between items-center px-4 py-2.5 bg-muted/20">
                                  <Badge variant="secondary" className="font-normal ml-2 bg-[#E8F2F4] text-[#374349] dark:bg-primary/15 dark:text-foreground">
                                    {SgIcon && <SgIcon className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-primary" />}
                                    {quoteHeadingLabel(quote.headingNames, label, "subgroup")}
                                  </Badge>
                                  <span className="text-sm font-medium">{formatCurrency(sgTotal)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()
            ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className={isEditMode ? "min-w-[200px]" : (showQtyUnitsView ? "" : "sm:w-[45%]")}>Item</TableHead>
                    {/* Mobile display view stacks category/subtotal under the description */}
                    <TableHead className={isEditMode ? undefined : "hidden sm:table-cell"}>Category</TableHead>
                    {(isEditMode || showQtyUnitsView) && (
                      <>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead className="text-right">Unit Cost</TableHead>
                      </>
                    )}
                    {showSubtotalColumn && (
                      <TableHead className={`text-right${isEditMode ? "" : " pr-6 hidden sm:table-cell"}`}>Subtotal</TableHead>
                    )}
                    {isEditMode && <TableHead className="w-10" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isEditMode ? (
                    visibleEditItems.map(({ item, index }) => {
                      const itemSubtotal = quotedMoney((parseFloat(item.quantity) || 0) * (parseFloat(item.unitCost) || 0));
                      return (
                        <Fragment key={index}>
                        <TableRow className="align-top" data-testid={`row-edit-${index}`}>
                          <TableCell className="py-2">
                            <Input
                              value={item.name}
                              onChange={(e) => updateItem(index, "name", e.target.value)}
                              placeholder="Item name"
                              className="h-8 text-sm font-medium mb-1"
                              data-testid={`input-name-${index}`}
                            />
                            <Textarea
                              value={item.description}
                              onChange={(e) => updateItem(index, "description", e.target.value)}
                              placeholder="Description (optional)"
                              className="text-xs text-muted-foreground min-h-[52px] resize-none"
                              rows={2}
                              data-testid={`input-desc-${index}`}
                            />
                            {isMultiScopeQuote && (
                              <div className="mt-1.5 flex items-center gap-1.5">
                                <span className="text-[11px] text-muted-foreground shrink-0">Scope:</span>
                                <Select
                                  value={item.scopeName ?? SHARED_SCOPE_NAME}
                                  onValueChange={(v) => updateItem(index, "scopeName", v)}
                                >
                                  <SelectTrigger className="h-7 text-xs" data-testid={`select-scope-${index}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {scopeNames.map(sn => (
                                      <SelectItem key={sn} value={sn}>{sn}</SelectItem>
                                    ))}
                                    <SelectItem value={SHARED_SCOPE_NAME}>Shared site costs</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            <div className="mt-1.5 flex items-center gap-1.5">
                              <Checkbox
                                id={`optional-edit-${index}`}
                                checked={item.isOptional}
                                onCheckedChange={(v) => updateItem(index, "isOptional", !!v)}
                                data-testid={`checkbox-optional-${index}`}
                              />
                              <label htmlFor={`optional-edit-${index}`} className="text-xs text-muted-foreground cursor-pointer">Optional works</label>
                            </div>
                          </TableCell>
                          <TableCell className="py-2 min-w-[140px]">
                            <Select value={item.category} onValueChange={(v) => updateItem(index, "category", v)}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`select-category-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {COST_CATEGORIES.map(c => (
                                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="py-2 text-right min-w-[70px]">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, "quantity", e.target.value)}
                              className="h-8 text-sm text-right w-20"
                              data-testid={`input-qty-${index}`}
                            />
                          </TableCell>
                          <TableCell className="py-2 min-w-[90px]">
                            <Select value={item.unit} onValueChange={(v) => updateItem(index, "unit", v)}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`select-unit-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(getUnits(market)).map(([val, label]) => (
                                  <SelectItem key={val} value={val}>{label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="py-2 text-right min-w-[100px]">
                            <div className="relative">
                              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">{getMarketCurrency(user?.market).symbol}</span>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unitCost}
                                onChange={(e) => updateItem(index, "unitCost", e.target.value)}
                                className={`h-8 text-sm text-right ${getMarketCurrency(user?.market).symbol.length >= 2 ? "pl-9 w-32" : "pl-6 w-28"}`}
                                data-testid={`input-cost-${index}`}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="py-2 text-right font-medium text-sm">
                            {formatCurrency(itemSubtotal)}
                          </TableCell>
                          <TableCell className="py-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => removeItem(index)}
                              data-testid={`button-delete-${index}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        {item.quantityDerivation && <TableRow><TableCell colSpan={7}>
                          <QuoteDimensions data={item.quantityDerivation} quantity={Number(item.quantity)} unit={item.unit} recipe={item.quoteCostRecipe} itemName={item.name}
                            specification={`${item.name} ${item.description ?? ""}`}
                            onChange={(data, quantity) => setEditItems(prev => prev.map((line, i) => {
                              if (i !== index) return line;
                              const next = applyQuotedPriceEdit(line, { ...line, quantity: String(quantity), quantityDerivation: data });
                              if (next.quoteCostRecipe) {
                                if (next.quoteCostRecipe.quote_pricing?.status !== "manual") {
                                  next.quoteCostRecipe = priceRecipeForQuote(next.quoteCostRecipe, Number(next.unitCost), data, `${next.name} ${next.description ?? ""}`, Number(next.quantity), next.unit);
                                  next.unitCost = deriveCostRecipe(next.quoteCostRecipe).rateTypical;
                                }
                                const split = quoteRecipeSplit(next.quoteCostRecipe, quantity);
                                return { ...next, labourCost: String(split.labourCost), materialCost: String(split.materialCost), plantCost: String(split.plantCost) };
                              }
                              return applyQuotedPriceEdit(line, { ...next,
                                quantityDerivation: markDimensionPriceReview(line.quantityDerivation, data, line.unit) });
                            }))} />
                          {item.quoteCostRecipe?.quote_pricing && <QuoteMaterialPricing recipe={item.quoteCostRecipe}
                            onChange={recipe => {
                              try {
                                const priced = priceRecipeForQuote(recipe, Number(item.unitCost), item.quantityDerivation, `${item.name} ${item.description ?? ""}`, Number(item.quantity), item.unit);
                                const split = quoteRecipeSplit(priced, Number(item.quantity));
                                setEditItems(prev => prev.map((line, i) => i !== index ? line : {
                                  ...line, quoteCostRecipe: priced, unitCost: deriveCostRecipe(priced).rateTypical,
                                  labourCost: String(split.labourCost), materialCost: String(split.materialCost), plantCost: String(split.plantCost),
                                }));
                              } catch (error) {
                                toast({ title: "Unable to calculate materials", description: error instanceof Error ? error.message : "Check the measurements", variant: "destructive" });
                              }
                            }} />}
                        </TableCell></TableRow>}
                        <TableRow data-testid={`row-edit-breakdown-${index}`}>
                          <TableCell colSpan={7} className="pt-0 pb-3 text-xs text-muted-foreground">
                            Labour {formatCurrency(Number(item.labourCost))} · Materials {formatCurrency(Number(item.materialCost))} · Plant {formatCurrency(Number(item.plantCost))}
                          </TableCell>
                        </TableRow>
                        </Fragment>
                      );
                    })
                  ) : (
                    visibleDisplayItems.filter(item => !(item as any).isOptional).map((item) => {
                      const lab = Number(item.labourCost || 0);
                      const mat = Number(item.materialCost || 0);
                      const plt = Number(item.plantCost || 0);
                      const showThisBreakdown = showBreakdownView && (lab > 0 || mat > 0 || plt > 0);
                      return (
                        <Fragment key={item.id}>
                          <TableRow data-testid={`row-lineitem-${item.id}`} className={`group ${showThisBreakdown ? "border-b-0" : ""}`}>
                            <TableCell className={showQtyUnitsView ? undefined : "sm:w-[50%]"}>
                              <div className="font-medium">
                                {item.name.replace(/\b\w/g, l => l.toUpperCase())}
                              </div>
                              {item.description && showDescriptionsView && (
                                <div className="text-sm text-muted-foreground">
                                  {seDesignConfirmed
                                    ? item.description.replace(` ${STRUCTURAL_DISCLAIMER}`, "").replace(STRUCTURAL_DISCLAIMER, "").trim()
                                    : item.description}
                                </div>
                              )}
                              {/* Mobile only: category + subtotal stacked under the text */}
                              <div className="flex sm:hidden items-center justify-between gap-2 mt-2">
                                <Badge variant="secondary" className="font-normal bg-[#E8F2F4] text-[#374349] dark:bg-primary/15 dark:text-foreground">
                                  {(() => { const Icon = getCategoryIcon(item.category); return Icon ? <Icon className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-primary" /> : null; })()}
                                  {quoteCategoryLabel(quote.headingNames, item, getCategoryLabel(item.category).replace(/\b\w/g, l => l.toUpperCase()))}
                                </Badge>
                                {showSubtotalColumn && (
                                  <span className="font-medium text-sm shrink-0">{formatCurrency(item.subtotal)}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <Badge variant="secondary" className="font-normal bg-[#E8F2F4] text-[#374349] dark:bg-primary/15 dark:text-foreground">
                                {(() => { const Icon = getCategoryIcon(item.category); return Icon ? <Icon className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-primary" /> : null; })()}
                                {quoteCategoryLabel(quote.headingNames, item, getCategoryLabel(item.category).replace(/\b\w/g, l => l.toUpperCase()))}
                              </Badge>
                            </TableCell>
                            {showQtyUnitsView && (
                              <>
                                <TableCell className="text-right">{item.quantity}</TableCell>
                                <TableCell>{getUnitLabel(item.unit)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(item.unitCost)}</TableCell>
                              </>
                            )}
                            {showSubtotalColumn && (
                              <TableCell className="text-right font-medium pr-6 hidden sm:table-cell">
                                {formatCurrency(item.subtotal)}
                              </TableCell>
                            )}
                          </TableRow>
                          {showThisBreakdown && (
                            <TableRow data-testid={`row-breakdown-${item.id}`}>
                              {/* colSpan = visible columns so the right-aligned
                                  breakdown sits under Subtotal. max-w-full +
                                  overflow-x-auto means if it's ever too wide
                                  only this little row scrolls, not the table.
                                  pr-6 matches the pr-6 added to the Subtotal
                                  TableHead/TableCell so left/right gutters look
                                  symmetric inside the Card. */}
                              <TableCell colSpan={visibleColCount} className="pt-0 pb-3 pr-6">
                                <div className="flex justify-start sm:justify-end sm:pr-2">
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground sm:flex-nowrap sm:whitespace-nowrap sm:max-w-full sm:overflow-x-auto">
                                    <span className="inline-flex items-center gap-1.5"><HardHat className="h-3.5 w-3.5 text-primary" />Labour <span className="font-medium text-foreground/80">{formatCurrency(lab)}</span></span>
                                    <span className="text-muted-foreground/60">·</span>
                                    <span className="inline-flex items-center gap-1.5"><BrickWall className="h-3.5 w-3.5 text-primary" />Material <span className="font-medium text-foreground/80">{formatCurrency(mat)}</span></span>
                                    <span className="text-muted-foreground/60">·</span>
                                    <span className="inline-flex items-center gap-1.5"><Construction className="h-3.5 w-3.5 text-primary" />Plant <span className="font-medium text-foreground/80">{formatCurrency(plt)}</span></span>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>

              {isEditMode && (
                <div className="pt-4 border-t mt-2">
                  <Button variant="outline" size="sm" onClick={addItem} className="w-full border-dashed" data-testid="button-add-item">
                    <Plus className="h-3.5 w-3.5 mr-2" />
                    Add Item
                  </Button>
                </div>
              )}
            </div>
            )}
            {/* Optional Works section — excluded from quoted price */}
            {(() => {
              const optionalDisplayItems = visibleDisplayItems.filter(item => !!(item as any).isOptional);
              if (optionalDisplayItems.length === 0 && !isEditMode) return null;
              if (isEditMode) {
                // In edit mode, optional items are already shown inline with the Optional checkbox — no separate section needed.
                return null;
              }
              const optionalTotal = optionalDisplayItems.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.unitCost)), 0);
              return (
                <details className="group/optional mt-4 border border-amber-300/40 dark:border-amber-700/40 rounded-lg overflow-hidden" data-testid="section-optional-works">
                  <summary className="cursor-pointer list-none flex items-center gap-2 py-3 px-4 bg-amber-50/60 dark:bg-amber-950/20 [&::-webkit-details-marker]:hidden">
                    <CircleDashed className="h-4 w-4 text-amber-500 shrink-0" />
                    <span className="font-semibold text-sm flex-1 text-amber-700 dark:text-amber-400">Optional Works</span>
                    <span className="text-xs text-amber-500/60">not included in quoted price</span>
                    {showSubtotalColumn && (
                      <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 ml-2">{formatCurrency(optionalTotal)}</span>
                    )}
                    <span aria-hidden="true" className="text-amber-600 transition-transform group-open/optional:rotate-90">›</span>
                  </summary>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className={showQtyUnitsView ? "" : "sm:w-[45%]"}>Item</TableHead>
                          <TableHead className="hidden sm:table-cell">Category</TableHead>
                          {showQtyUnitsView && (<><TableHead className="text-right">Qty</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Unit Cost</TableHead></>)}
                          {showSubtotalColumn && <TableHead className="text-right pr-6 hidden sm:table-cell">Subtotal</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {optionalDisplayItems.map((item) => {
                          const lab = Number(item.labourCost || 0);
                          const mat = Number(item.materialCost || 0);
                          const plt = Number(item.plantCost || 0);
                          const showThisBreakdown = showBreakdownView && (lab > 0 || mat > 0 || plt > 0);
                          return (
                            <Fragment key={item.id}>
                              <TableRow data-testid={`row-optional-${item.id}`} className={`group ${showThisBreakdown ? "border-b-0" : ""}`}>
                                <TableCell className={showQtyUnitsView ? undefined : "sm:w-[50%]"}>
                                  <div className="font-medium">{item.name.replace(/\b\w/g, l => l.toUpperCase())}</div>
                                  {item.description && showDescriptionsView && (
                                    <div className="text-sm text-muted-foreground">{item.description}</div>
                                  )}
                                  {/* Mobile only: category + subtotal stacked under the text */}
                                  <div className="flex sm:hidden items-center justify-between gap-2 mt-2">
                                    <Badge variant="secondary" className="font-normal bg-[#E8F2F4] text-[#374349] dark:bg-primary/15 dark:text-foreground">
                                      {(() => { const Icon = getCategoryIcon(item.category); return Icon ? <Icon className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-primary" /> : null; })()}
                                      {getCategoryLabel(item.category).replace(/\b\w/g, l => l.toUpperCase())}
                                    </Badge>
                                    {showSubtotalColumn && (
                                      <span className="font-medium text-sm shrink-0">{formatCurrency(item.subtotal)}</span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="hidden sm:table-cell">
                                  <Badge variant="secondary" className="font-normal bg-[#E8F2F4] text-[#374349] dark:bg-primary/15 dark:text-foreground">
                                    {(() => { const Icon = getCategoryIcon(item.category); return Icon ? <Icon className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-primary" /> : null; })()}
                                    {getCategoryLabel(item.category).replace(/\b\w/g, l => l.toUpperCase())}
                                  </Badge>
                                </TableCell>
                                {showQtyUnitsView && (<><TableCell className="text-right">{item.quantity}</TableCell><TableCell>{getUnitLabel(item.unit)}</TableCell><TableCell className="text-right">{formatCurrency(item.unitCost)}</TableCell></>)}
                                {showSubtotalColumn && <TableCell className="text-right font-medium pr-6 hidden sm:table-cell">{formatCurrency(item.subtotal)}</TableCell>}
                              </TableRow>
                              {showThisBreakdown && (
                                <TableRow data-testid={`row-breakdown-optional-${item.id}`}>
                                  <TableCell colSpan={visibleColCount} className="pt-0 pb-3 pr-6">
                                    <div className="flex justify-start sm:justify-end sm:pr-2">
                                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground sm:flex-nowrap sm:whitespace-nowrap sm:max-w-full sm:overflow-x-auto">
                                        <span className="inline-flex items-center gap-1.5"><HardHat className="h-3.5 w-3.5 text-primary" />Labour <span className="font-medium text-foreground/80">{formatCurrency(lab)}</span></span>
                                        <span className="text-muted-foreground/60">·</span>
                                        <span className="inline-flex items-center gap-1.5"><BrickWall className="h-3.5 w-3.5 text-primary" />Material <span className="font-medium text-foreground/80">{formatCurrency(mat)}</span></span>
                                        <span className="text-muted-foreground/60">·</span>
                                        <span className="inline-flex items-center gap-1.5"><Construction className="h-3.5 w-3.5 text-primary" />Plant <span className="font-medium text-foreground/80">{formatCurrency(plt)}</span></span>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </details>
              );
            })()}
            </>
            );
          })()}
        </CardContent>
      </Card>

      <QuoteScopeReview notes={(quote as typeof quote & { reviewerNotes?: string[] }).reviewerNotes}
        editHref={`/quotes/${quote.id}/edit`} />
      {(quote.quoteType === "auto" || (quote.quoteType === "template" && market === "uk")) && <>
        <QuoteMaterialsWorkspace>
        <QuantityMaterialsReview autoSuggest={isEditMode} sessionKey={user?.id}
          onJump={index => {
            if (!isEditMode) enterEditMode();
            setActiveScopeTab("__combined__");
            setTimeout(() => document.querySelector(`[data-testid="input-name-${index}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
          }}
          items={(isEditMode ? editItems : displayLineItems).map(i => ({
          name: i.name, quantity: Number(i.quantity), unit: i.unit, isOptional: i.isOptional ?? false,
          category: i.category, description: i.description, unitCost: i.unitCost, materialCost: i.materialCost,
          materialAllowanceApproval: i.materialAllowanceApproval,
          quantityDerivation: i.quantityDerivation as QuantityDerivation | null,
          quoteCostRecipe: i.quoteCostRecipe as CostRecipe | null,
        }))} onAllowanceChange={(index, approval) => {
          setEditItems(prev => (isEditMode ? prev : displayLineItems.map(toEditable)).map((line, i) =>
            i === index ? { ...line, materialAllowanceApproval: approval } : line));
          setIsEditMode(true);
        }} />
        <QuoteMaterialsList items={isEditMode ? editItems : displayLineItems}
          reference={quote.quoteNumber} quoteId={id} saved={!isEditMode} saving={saveEditsMutation.isPending}
          onApprovalChange={(index, approval) => {
            setEditItems(prev => (isEditMode ? prev : displayLineItems.map(toEditable)).map((line, i) =>
              i === index ? { ...line, materialAllowanceApproval: approval } : line));
            setIsEditMode(true);
          }}
          onPriceChange={(materialId, price) => {
            try {
              const source = isEditMode ? editItems : displayLineItems.map(toEditable);
              const old = source.flatMap(i => i.quoteCostRecipe?.materials ?? []).find(m => m.material_id === materialId)?.unit_price ?? 0;
              const result = updateMaterialPrice(source, materialId, price);
              setEditItems(result.lines.map(i => ({ ...i, unitCost: String(i.unitCost), labourCost: String(i.labourCost), materialCost: String(i.materialCost), plantCost: String(i.plantCost) })));
              setIsEditMode(true);
              toast({ title: `Updated £${old.toFixed(2)} → £${price.toFixed(2)} on ${result.affected} ${result.affected === 1 ? "line" : "lines"}.` });
            } catch (error) { toast({ title: error instanceof Error ? error.message : "Could not update material price", variant: "destructive" }); }
          }}
          onRoundingChange={(row, enabled) => {
            setEditItems(setMaterialPackRounding(isEditMode ? editItems : displayLineItems.map(toEditable), row, enabled));
            setIsEditMode(true);
          }}
          onOrderQuantityChange={(row, quantity) => {
            setEditItems(setMaterialOrderQuantity(isEditMode ? editItems : displayLineItems.map(toEditable), row, quantity));
            setIsEditMode(true);
          }}
          onJump={index => {
            if (!isEditMode) enterEditMode();
            setActiveScopeTab("__combined__");
            setTimeout(() => document.querySelector(`[data-testid="input-name-${index}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
          }} />
        </QuoteMaterialsWorkspace>
        {isEditMode && <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={saveEditsMutation.isPending} onClick={discardEdits}>Discard changes</Button>
          <Button disabled={saveEditsMutation.isPending} onClick={() => saveEditsMutation.mutate(editItems)}>
            {saveEditsMutation.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>}
      </>}
      {/* Assumptions & Exclusions */}
      {!isEditMode && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {quote.assumptions && (
            <Card>
              <CardHeader><CardTitle>Assumptions</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {quote.assumptions.split("\n").filter(l => l.trim()).map((line, i) => (
                    <p key={i} className="text-sm">{line}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {quote.exclusions && (
            <Card>
              <CardHeader><CardTitle>Exclusions</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {quote.exclusions.split("\n").filter(l => l.trim()).map((line, i) => (
                    <p key={i} className="text-sm">{line}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Outgoing Shares */}
      {!isEditMode && outgoingShares.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Shared With</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setIsShareDialogOpen(true)} data-testid="button-share-again">
                <Share2 className="h-3.5 w-3.5 mr-1.5" />
                Share Again
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {outgoingShares.map(share => (
                <div key={share.id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {share.recipientKind === "customer" ? (
                      <User className="h-4 w-4 text-muted-foreground shrink-0" />
                    ) : (
                      <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="text-sm truncate">{share.recipientEmail}</span>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                      {share.recipientKind === "customer" ? "Customer" : "Builder"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-muted-foreground">
                      {new Date(share.createdAt).toLocaleDateString("en-GB")}
                    </span>
                    <ShareStatusBadge status={share.status} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* QuickBooks Action */}
      {!isEditMode && outgoingShares.some(s => s.recipientKind === 'customer' && s.status === 'accepted') && (
        <QuickBooksQuoteAction quoteId={id} outgoingShares={outgoingShares} />
      )}

      {/* Share Dialog */}
      <Dialog open={isShareDialogOpen} onOpenChange={(open) => {
        setIsShareDialogOpen(open);
        if (!open) {
          setShareEmail("");
          setCustomerEmail("");
          setCustomerMessage("");
          setShareTab("customer");
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Share Quote</DialogTitle>
            <DialogDescription>
              Send this quote to your customer by email, or share it with another user who has an account.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={shareTab} onValueChange={(v) => setShareTab(v as "builder" | "customer")} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="customer" data-testid="tab-share-customer">
                <Mail className="h-4 w-4 mr-2" /> Send To Customer
              </TabsTrigger>
              <TabsTrigger value="builder" data-testid="tab-share-builder">
                <Building2 className="h-4 w-4 mr-2" /> Share With User
              </TabsTrigger>
            </TabsList>

            {/* Customer Tab */}
            <TabsContent value="customer" className="space-y-4 pt-3">
              <div className="space-y-1.5">
                <Label htmlFor="customer-email">Customer's email address</Label>
                <Input
                  id="customer-email"
                  type="email"
                  placeholder="customer@example.com"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  data-testid="input-customer-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="customer-message">Personal message (optional)</Label>
                <Textarea
                  id="customer-message"
                  placeholder="Hi, please find your quote attached. Let me know if you have any questions."
                  value={customerMessage}
                  onChange={(e) => setCustomerMessage(e.target.value)}
                  rows={3}
                  data-testid="textarea-customer-message"
                />
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <span>An email is sent to your customer with the quote attached as a PDF</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  <span>They also get a link to view it online and accept or request changes</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  <span>Replies go straight to your email so you can respond directly</span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsShareDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={() => sendToCustomerMutation.mutate({ email: customerEmail, message: customerMessage })}
                  disabled={!customerEmail || sendToCustomerMutation.isPending}
                  data-testid="button-send-customer"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sendToCustomerMutation.isPending ? "Sending..." : "Send Quote"}
                </Button>
              </DialogFooter>
            </TabsContent>

            {/* Builder Tab */}
            <TabsContent value="builder" className="space-y-4 pt-3">
              <div className="space-y-1.5">
                <Label htmlFor="share-email">User's account email</Label>
                <Input
                  id="share-email"
                  type="email"
                  placeholder="user@example.com"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  data-testid="input-share-email"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && shareEmail) shareMutation.mutate({ email: shareEmail });
                  }}
                />
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5 shrink-0" />
                  <span>The recipient must already have an account on The Proper Tool</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  <span>The quote appears instantly in their account, marked as "Shared With You"</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  <span>You keep your original — sharing creates a separate copy for them</span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsShareDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={() => shareMutation.mutate({ email: shareEmail })}
                  disabled={!shareEmail || shareMutation.isPending}
                  data-testid="button-send-share"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  {shareMutation.isPending ? "Sharing..." : "Share Quote"}
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShareStatusBadge({ status }: { status: string }) {
  if (status === "accepted") return <Badge variant="default" className="text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">Accepted</Badge>;
  if (status === "changes_requested") return <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 dark:text-amber-400">Changes Requested</Badge>;
  if (status === "rejected") return <Badge variant="secondary" className="text-xs">Declined</Badge>;
  if (status === "delivered") return <Badge variant="outline" className="text-xs border-violet-400 text-violet-600 dark:text-violet-400">Delivered</Badge>;
  if (status === "opened") return <Badge variant="outline" className="text-xs border-blue-400 text-blue-600 dark:text-blue-400">Opened</Badge>;
  if (status === "sent") return <Badge variant="outline" className="text-xs border-violet-400 text-violet-600 dark:text-violet-400">Sent</Badge>;
  return <Badge variant="outline" className="text-xs">{status}</Badge>;
}

function QuickBooksQuoteAction({ quoteId, outgoingShares }: { quoteId: string, outgoingShares: any[] }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const isGenuineAccepted = useMemo(() => {
    return outgoingShares.some(s => s.recipientKind === 'customer' && s.status === 'accepted');
  }, [outgoingShares]);

  const { data: qbData, isLoading } = useQuery<{ eligible: boolean, connected: boolean, configured: boolean, issues: string[], invoice: { id: string, docNumber: string | null, status: string, createdAt: string, url: string } | null, syncStatus: string | null, lastError: string | null }>({
    queryKey: ["/api/quotes", quoteId, "quickbooks"],
    enabled: isGenuineAccepted,
    refetchInterval: (query) => query.state.data?.syncStatus === "in_progress" ? 3000 : false,
  });

  const createInvoice = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/quotes/${quoteId}/quickbooks`, {});
      return await res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", quoteId, "quickbooks"] });
      if (data.syncStatus === "failed") {
        toast({ title: "Failed to create invoice", description: data.lastError || "An error occurred with QuickBooks.", variant: "destructive" });
      } else if (data.syncStatus === "in_progress") {
        toast({ title: "Invoice creation is already in progress", description: "The QuickBooks status will update when it finishes." });
      } else if (data.syncStatus === "succeeded" && data.invoice) {
        toast({ title: "Invoice Created", description: "Your QuickBooks invoice has been created successfully." });
      } else {
        toast({ title: "QuickBooks did not create the invoice", description: "Review the quote details and try again.", variant: "destructive" });
      }
    },
    onError: (err: any) => {
      toast({ title: "Failed to create invoice", description: err.message, variant: "destructive" });
    }
  });

  if (!isGenuineAccepted) return null;

  return (
    <Card className="mt-4 border-emerald-100 dark:border-emerald-900/30" data-testid="card-quickbooks-action">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-foreground">
          <Landmark className="h-4 w-4" />
          QuickBooks Invoicing
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking QuickBooks status...
          </div>
        ) : !qbData ? (
          <p className="text-sm text-muted-foreground">Status unavailable.</p>
        ) : !qbData.connected ? (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 border rounded-lg bg-card">
            <div className="space-y-1">
              <p className="text-sm font-medium">QuickBooks is disconnected</p>
              <p className="text-sm text-muted-foreground">Connect to QuickBooks in Settings to create an invoice for this quote.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/settings")}>
              Connect QuickBooks
            </Button>
          </div>
        ) : qbData.invoice ? (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/30 rounded-lg">
            <div>
              <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Invoice Created
              </p>
              <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5">
                {qbData.invoice.docNumber ? `Invoice #${qbData.invoice.docNumber}` : "Invoice has been created."}
              </p>
            </div>
            <Button size="sm" variant="outline" className="bg-white hover:bg-gray-50 text-emerald-700 border-emerald-200 dark:bg-transparent dark:text-emerald-400 dark:border-emerald-800" asChild>
              <a href={qbData.invoice.url} target="_blank" rel="noopener noreferrer">View in QuickBooks</a>
            </Button>
          </div>
        ) : qbData.issues && qbData.issues.length > 0 ? (
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 p-4 border rounded-lg bg-card">
            <div className="space-y-3 flex-1">
              <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg border border-amber-200 dark:border-amber-900/50">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium mb-1">Cannot create invoice yet</p>
                  <ul className="list-disc pl-4 space-y-1">
                    {qbData.issues.map((issue, i) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 border rounded-lg bg-card">
            <div>
              <p className="text-sm font-medium">Ready to invoice</p>
              <p className="text-sm text-muted-foreground mt-0.5">Create an invoice in QuickBooks Online with these items.</p>
              {qbData.lastError && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {qbData.lastError}
                </p>
              )}
            </div>
            <Button
              onClick={() => createInvoice.mutate()}
              disabled={createInvoice.isPending || (qbData.syncStatus === "in_progress")}
              data-testid="button-create-invoice"
              className="w-full sm:w-auto shrink-0"
            >
              {createInvoice.isPending || qbData.syncStatus === "in_progress" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create in QuickBooks"
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
